import { cn } from '@/lib/utils'

interface FitlabLogoProps {
  /** 'horizontal' (default) = corazón + wordmark "Fitlab". 'icon' = solo corazón con corredor. */
  variant?: 'horizontal' | 'icon'
  /** Mantiene compat con llamadas previas que pasaban 'color' | 'white'. Si llega 'white', aplica filtro brightness para usar en footers oscuros. */
  tone?: 'color' | 'white'
  /** Compat legacy: showWordmark=false → variant 'icon'. */
  showWordmark?: boolean
  role?: 'img' | 'none'
  className?: string
}

/**
 * Logo oficial FITLAB — bitmap PNG optimizado.
 *
 * Reemplaza el SVG estilizado reproducido manualmente. Los assets viven en
 * /web/public/brand/ y fueron generados a partir del PNG oficial entregado
 * por el cliente (docs/brand-fitlab/logo_fitlab.png).
 *
 * Variantes:
 *  - horizontal: 800x297 — corazón tricolor + wordmark "Fitlab"
 *  - icon: 240x240 — solo corazón con corredor (sin wordmark)
 *
 * `tone="white"` aplica filtro brightness(0) invert(1) para footers oscuros.
 * No es ideal (pierde gradiente tricolor) — TODO: cliente provea versión
 * monocromática blanca oficial.
 */
export default function FitlabLogo({
  variant,
  tone = 'color',
  showWordmark,
  role = 'img',
  className,
}: FitlabLogoProps) {
  // Compat legacy: showWordmark=false → icon, default → horizontal.
  const effectiveVariant: 'horizontal' | 'icon' =
    variant ?? (showWordmark === false ? 'icon' : 'horizontal')

  const src = effectiveVariant === 'icon' ? '/brand/fitlab-logo-icon.png' : '/brand/fitlab-logo.png'

  const ariaProps =
    role === 'img'
      ? { role: 'img' as const, 'aria-label': 'FITLAB' }
      : { 'aria-hidden': true as const, role: 'presentation' as const }

  return (
    <img
      src={src}
      alt={role === 'img' ? 'FITLAB' : ''}
      {...ariaProps}
      className={cn(
        'block select-none object-contain',
        tone === 'white' && 'brightness-0 invert',
        className,
      )}
      draggable={false}
    />
  )
}
